import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { ChefHat, MapPin, Phone, Clock, Loader2, Plus, Minus, ShoppingCart, Gift, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function PublicMenu() {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [cart, setCart] = useState({});
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [placed, setPlaced] = useState(null);
  const [form, setForm] = useState({ customer_name: "", phone: "", order_type: "pickup", table: "", notes: "", pickup_time: "", allergies: "", tip: 0, answers: {} });
  const [submitting, setSubmitting] = useState(false);

  const [giftOpen, setGiftOpen] = useState(false);
  const [giftAmounts, setGiftAmounts] = useState([]);
  const [gift, setGift] = useState({ amount: "", recipient_email: "", buyer_email: "", message: "" });
  const [giftLoading, setGiftLoading] = useState(false);
  const [giftCode, setGiftCode] = useState(null);

  useEffect(() => {
    api.get(`/public/menu/${slug}`).then((r) => setData(r.data)).catch(() => setErr(true));
    api.get(`/public/gift-cards/amounts`).then((r) => setGiftAmounts(r.data.amounts)).catch(() => {});
  }, [slug]);

  const pollGift = useCallback(async (sid, attempts = 0) => {
    if (attempts >= 6) { toast.error("Couldn't confirm payment."); setParams({}); return; }
    try {
      const { data: st } = await api.get(`/public/gift-cards/status/${sid}`);
      if (st.payment_status === "paid" && st.code) { setGiftCode(st.code); setParams({}); return; }
      if (st.status === "expired") { toast.error("Payment expired."); setParams({}); return; }
      setTimeout(() => pollGift(sid, attempts + 1), 2000);
    } catch { setParams({}); }
  }, [setParams]);

  useEffect(() => {
    const sid = params.get("gc_session_id");
    if (sid) { toast.message("Confirming your gift card payment..."); pollGift(sid); }
  }, [params, pollGift]);

  const pollOrder = useCallback(async (sid, attempts = 0) => {
    if (attempts >= 6) { toast.error("Couldn't confirm payment."); setParams({}); return; }
    try {
      const { data: st } = await api.get(`/public/order/status/${sid}`);
      if (st.payment_status === "paid") { setPlaced({ order_number: st.order_number, total: st.total }); setCart({}); setParams({}); return; }
      if (st.status === "expired") { toast.error("Payment expired."); setParams({}); return; }
      setTimeout(() => pollOrder(sid, attempts + 1), 2000);
    } catch { setParams({}); }
  }, [setParams]);

  useEffect(() => {
    const sid = params.get("order_session_id");
    if (sid) { toast.message("Confirming your payment..."); pollOrder(sid); }
  }, [params, pollOrder]);

  const items = useMemo(() => data?.items || [], [data]);
  const cartList = useMemo(() => items.filter((i) => cart[i.id]).map((i) => ({ ...i, qty: cart[i.id] })), [items, cart]);
  const total = cartList.reduce((s, i) => s + i.price * i.qty, 0);
  const count = cartList.reduce((s, i) => s + i.qty, 0);

  const add = (id) => setCart((c) => ({ ...c, [id]: (c[id] || 0) + 1 }));
  const sub = (id) => setCart((c) => { const n = (c[id] || 0) - 1; const next = { ...c }; if (n <= 0) delete next[id]; else next[id] = n; return next; });

  const placeOrder = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, tip: form.tip || 0, origin_url: window.location.origin, items: cartList.map((i) => ({ id: i.id, qty: i.qty })) };
      const { data: res } = await api.post(`/public/order/${slug}`, payload);
      if (res.payment_required && res.url) { window.location.href = res.url; return; }
      setPlaced(res); setCart({}); setCheckoutOpen(false);
    } catch (e2) { toast.error(apiErr(e2)); }
    setSubmitting(false);
  };

  const buyGift = async (e) => {
    e.preventDefault();
    setGiftLoading(true);
    try {
      const { data: res } = await api.post(`/public/gift-cards/checkout/${slug}`, {
        amount: parseFloat(gift.amount), recipient_email: gift.recipient_email,
        buyer_email: gift.buyer_email, message: gift.message, origin_url: window.location.origin,
      });
      window.location.href = res.url;
    } catch (e2) { toast.error(apiErr(e2)); setGiftLoading(false); }
  };

  if (err) return (
    <div className="min-h-screen flex items-center justify-center bg-background text-center p-6">
      <div><ChefHat className="h-10 w-10 text-muted-foreground mx-auto" /><p className="mt-3 text-muted-foreground">Menu not found.</p></div>
    </div>
  );
  if (!data) return (
    <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
  );

  const { restaurant: r } = data;
  const sc = r.site_content || {};
  const gallery = (sc.gallery || []).filter(Boolean);
  const heroTitle = sc.hero_title || r.name;
  const heroSubtitle = sc.hero_subtitle || r.description;
  const cats = [...new Set(items.map((i) => i.category))];
  const brand = r.brand_color || "#C86A51";
  const accent = r.accent_color || brand;
  const minimal = r.theme === "minimal";
  const cfg = r.order_form || { builtins: { phone: true, table: true }, custom_fields: [] };
  const bi = cfg.builtins || {};
  const customFields = cfg.custom_fields || [];
  const payReq = !!r.online_payment;
  const grandTotal = total + (form.tip || 0);
  const bg = minimal ? "#FBFAF7" : undefined;

  const solidBtn = { backgroundColor: brand, color: "#fff" };
  const outlineBtn = { borderColor: brand, color: brand };

  const Hero = minimal ? (
    <div className="text-center pt-14 pb-10 px-6 max-w-3xl mx-auto">
      {r.logo_url
        ? <img src={r.logo_url} alt={r.name} className="h-20 sm:h-24 mx-auto object-contain" />
        : <h1 className="font-heading text-5xl font-medium" style={{ color: brand }}>{heroTitle}</h1>}
      {r.logo_url && sc.hero_title && (
        <h1 className="font-heading text-3xl font-medium mt-5" style={{ color: brand }}>{sc.hero_title}</h1>
      )}
      <p className="mt-5 text-xs uppercase tracking-[0.35em] font-semibold" style={{ color: accent }}>{r.cuisine}</p>
      <div className="mx-auto mt-5 h-px w-16" style={{ backgroundColor: accent }} />
      <p className="mt-5 text-muted-foreground leading-relaxed max-w-xl mx-auto">{heroSubtitle}</p>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
        {r.address && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {r.address}</span>}
        {r.phone && <span className="flex items-center gap-1.5"><Phone className="h-4 w-4" /> {r.phone}</span>}
        {r.hours && <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> {r.hours}</span>}
      </div>
      <Button variant="outline" data-testid="buy-gift-card-btn" onClick={() => setGiftOpen(true)}
        className="mt-7 rounded-none border bg-transparent hover:bg-transparent hover:opacity-80 uppercase tracking-[0.15em] text-xs h-10 px-6" style={outlineBtn}>
        <Gift className="h-4 w-4 mr-2" /> Gift a card
      </Button>
      {r.cover_image && (
        <div className="mt-10 overflow-hidden">
          <img src={r.cover_image} alt="" className="w-full h-64 sm:h-80 object-cover" />
        </div>
      )}
    </div>
  ) : (
    <>
      <div className="relative h-56 sm:h-72">
        <img src={r.cover_image || "https://images.pexels.com/photos/29394552/pexels-photo-29394552.jpeg"} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/20" />
        <div className="absolute bottom-6 left-0 right-0 px-6 text-[#FDFBF7] max-w-3xl mx-auto">
          {r.logo_url && <img src={r.logo_url} alt="" className="h-12 mb-2 object-contain" />}
          <p className="text-xs uppercase tracking-[0.2em] font-bold opacity-90">{r.cuisine}</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-medium mt-1">{heroTitle}</h1>
          <p className="mt-1 opacity-90 text-sm">{heroSubtitle}</p>
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-6 py-4 flex flex-wrap items-center gap-4 text-sm text-muted-foreground border-b border-border">
        {r.address && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {r.address}</span>}
        {r.phone && <span className="flex items-center gap-1.5"><Phone className="h-4 w-4" /> {r.phone}</span>}
        {r.hours && <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> {r.hours}</span>}
        <Button size="sm" variant="outline" data-testid="buy-gift-card-btn" onClick={() => setGiftOpen(true)} className="ml-auto" style={outlineBtn}>
          <Gift className="h-4 w-4 mr-1.5" /> Buy a gift card
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen pb-28" style={{ backgroundColor: bg }}>
      <div className={minimal ? "bg-background" : ""} style={minimal ? { backgroundColor: bg } : undefined}>{Hero}</div>

      <div className="max-w-3xl mx-auto px-6 py-12 space-y-12">
        {sc.about_text && (
          <section data-testid="public-about" className="text-center max-w-2xl mx-auto">
            {minimal ? (
              <>
                <h2 className="font-heading text-3xl font-medium" style={{ color: brand }}>{sc.about_title || "Our Story"}</h2>
                <div className="mx-auto mt-3 h-px w-10" style={{ backgroundColor: accent }} />
              </>
            ) : (
              <h2 className="font-heading text-2xl font-medium mb-2">{sc.about_title || "Our Story"}</h2>
            )}
            <p className="mt-5 text-muted-foreground leading-relaxed whitespace-pre-line">{sc.about_text}</p>
          </section>
        )}
        {cats.map((cat) => (
          <div key={cat}>
            {minimal ? (
              <div className="text-center mb-7">
                <h2 className="font-heading text-3xl font-medium" style={{ color: brand }}>{cat}</h2>
                <div className="mx-auto mt-3 h-px w-10" style={{ backgroundColor: accent }} />
              </div>
            ) : (
              <h2 className="font-heading text-2xl font-medium border-b border-border pb-2 mb-4">{cat}</h2>
            )}
            <div className={minimal ? "space-y-6" : "space-y-4"}>
              {items.filter((i) => i.category === cat).map((it) => (
                <div key={it.id} data-testid={`public-item-${it.id}`} className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-baseline gap-2">
                      <p className={minimal ? "font-heading text-lg font-medium" : "font-medium"}>{it.name}</p>
                      {minimal && <span className="flex-1 border-b border-dotted border-stone-300 translate-y-[-3px]" />}
                      <span className="font-heading text-lg font-semibold" style={{ color: brand }}>${Number(it.price).toFixed(2)}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5 max-w-md">{it.description}</p>
                  </div>
                  {cart[it.id] ? (
                    <div className="flex items-center gap-2 shrink-0">
                      <Button size="icon" variant="outline" className="h-8 w-8" style={outlineBtn} onClick={() => sub(it.id)} data-testid={`dec-${it.id}`}><Minus className="h-4 w-4" /></Button>
                      <span className="w-5 text-center font-medium" data-testid={`qty-${it.id}`}>{cart[it.id]}</span>
                      <Button size="icon" className="h-8 w-8" style={solidBtn} onClick={() => add(it.id)} data-testid={`inc-${it.id}`}><Plus className="h-4 w-4" /></Button>
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" className={`shrink-0 ${minimal ? "rounded-none uppercase tracking-[0.1em] text-xs" : ""}`} style={outlineBtn} onClick={() => add(it.id)} data-testid={`add-${it.id}`}>
                      <Plus className="h-4 w-4 mr-1" /> Add
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-center text-muted-foreground">This menu is being prepared. Check back soon!</p>}

        {gallery.length > 0 && (
          <section data-testid="public-gallery">
            {minimal ? (
              <div className="text-center mb-7">
                <h2 className="font-heading text-3xl font-medium" style={{ color: brand }}>Gallery</h2>
                <div className="mx-auto mt-3 h-px w-10" style={{ backgroundColor: accent }} />
              </div>
            ) : (
              <h2 className="font-heading text-2xl font-medium border-b border-border pb-2 mb-4">Gallery</h2>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {gallery.map((src, i) => (
                <img key={src} src={src} alt="" loading="lazy" data-testid={`gallery-img-${i}`}
                  className="w-full aspect-square object-cover rounded-lg" />
              ))}
            </div>
          </section>
        )}
      </div>

      <footer className="text-center py-10 border-t border-border">
        {r.logo_url && minimal && <img src={r.logo_url} alt="" className="h-10 mx-auto object-contain opacity-70 mb-3" />}
        {(sc.instagram || sc.facebook || sc.website_url) && (
          <div className="flex items-center justify-center gap-5 mb-4 text-sm" data-testid="public-social">
            {sc.instagram && <a href={sc.instagram} target="_blank" rel="noreferrer" className="hover:underline" style={{ color: brand }}>Instagram</a>}
            {sc.facebook && <a href={sc.facebook} target="_blank" rel="noreferrer" className="hover:underline" style={{ color: brand }}>Facebook</a>}
            {sc.website_url && <a href={sc.website_url} target="_blank" rel="noreferrer" className="hover:underline" style={{ color: brand }}>Website</a>}
          </div>
        )}
        <span className="flex items-center justify-center gap-1.5 text-sm text-muted-foreground"><ChefHat className="h-4 w-4" style={{ color: brand }} /> Powered by manage.kitchen</span>
      </footer>

      {count > 0 && (
        <div className="fixed bottom-4 left-0 right-0 px-4 z-40">
          <div className="max-w-3xl mx-auto">
            <Button data-testid="view-cart-btn" onClick={() => setCheckoutOpen(true)} style={solidBtn}
              className="w-full h-14 shadow-[0_12px_40px_rgba(0,0,0,0.25)] flex items-center justify-between px-6 text-base hover:opacity-90">
              <span className="flex items-center gap-2"><ShoppingCart className="h-5 w-5" /> {count} item{count > 1 ? "s" : ""}</span>
              <span className="font-heading text-lg font-semibold">${total.toFixed(2)} · Checkout</span>
            </Button>
          </div>
        </div>
      )}

      <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <DialogContent className="bg-card max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Your order</DialogTitle></DialogHeader>
          <div className="space-y-2 mb-2">
            {cartList.map((i) => (
              <div key={i.id} className="flex justify-between text-sm"><span>{i.qty}× {i.name}</span><span>${(i.price * i.qty).toFixed(2)}</span></div>
            ))}
            <div className="flex justify-between text-sm pt-2 border-t border-border"><span>Subtotal</span><span>${total.toFixed(2)}</span></div>
            {form.tip > 0 && <div className="flex justify-between text-sm"><span>Tip</span><span>${form.tip.toFixed(2)}</span></div>}
            <div className="flex justify-between font-semibold"><span>Total</span><span className="font-heading text-lg">${grandTotal.toFixed(2)}</span></div>
          </div>
          <form onSubmit={placeOrder} className="space-y-4">
            <div className="space-y-1.5"><Label>Your name</Label>
              <Input data-testid="order-name" required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              {bi.phone !== false && (
                <div className="space-y-1.5"><Label>Phone</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              )}
              <div className="space-y-1.5"><Label>Type</Label>
                <Select value={form.order_type} onValueChange={(v) => setForm({ ...form, order_type: v })}>
                  <SelectTrigger data-testid="order-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pickup">Pickup</SelectItem>
                    <SelectItem value="dine-in">Dine-in</SelectItem>
                    <SelectItem value="delivery">Delivery</SelectItem>
                  </SelectContent>
                </Select></div>
            </div>
            {bi.table && form.order_type === "dine-in" && (
              <div className="space-y-1.5"><Label>Table number</Label>
                <Input value={form.table} onChange={(e) => setForm({ ...form, table: e.target.value })} /></div>
            )}
            {bi.pickup_time && form.order_type !== "dine-in" && (
              <div className="space-y-1.5"><Label>Preferred time</Label>
                <Input type="time" data-testid="order-pickup-time" value={form.pickup_time} onChange={(e) => setForm({ ...form, pickup_time: e.target.value })} /></div>
            )}
            {bi.allergies && (
              <div className="space-y-1.5"><Label>Allergies / dietary notes</Label>
                <Input value={form.allergies} onChange={(e) => setForm({ ...form, allergies: e.target.value })} /></div>
            )}
            {customFields.map((f) => (
              <div key={f.id} className="space-y-1.5" data-testid={`order-custom-${f.id}`}>
                <Label>{f.label}{f.required ? " *" : ""}</Label>
                {f.type === "dropdown" ? (
                  <Select value={form.answers[f.id] || ""} onValueChange={(v) => setForm({ ...form, answers: { ...form.answers, [f.id]: v } })}>
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>{(f.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                ) : f.type === "textarea" ? (
                  <Textarea required={f.required} value={form.answers[f.id] || ""} onChange={(e) => setForm({ ...form, answers: { ...form.answers, [f.id]: e.target.value } })} />
                ) : (
                  <Input required={f.required} value={form.answers[f.id] || ""} onChange={(e) => setForm({ ...form, answers: { ...form.answers, [f.id]: e.target.value } })} />
                )}
              </div>
            ))}
            <div className="space-y-1.5"><Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Special requests..." /></div>
            {bi.tip && (
              <div className="space-y-1.5"><Label>Add a tip</Label>
                <div className="grid grid-cols-4 gap-2">
                  {[0, 0.10, 0.15, 0.20].map((pct) => (
                    <button type="button" key={pct} data-testid={`tip-${pct}`} onClick={() => setForm({ ...form, tip: +(total * pct).toFixed(2) })}
                      className="py-2 rounded-lg border text-sm transition-colors"
                      style={Math.abs((form.tip || 0) - total * pct) < 0.005 ? { borderColor: brand, color: brand, backgroundColor: `${brand}12` } : {}}>
                      {pct === 0 ? "None" : `${pct * 100}%`}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button type="submit" data-testid="place-order-btn" disabled={submitting} style={solidBtn} className="w-full h-11 hover:opacity-90">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : payReq ? `Pay & order · $${grandTotal.toFixed(2)}` : `Place order · $${grandTotal.toFixed(2)}`}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!placed} onOpenChange={() => setPlaced(null)}>
        <DialogContent className="bg-card text-center" data-testid="order-success">
          <div className="py-4">
            <CheckCircle2 className="h-14 w-14 mx-auto" style={{ color: brand }} />
            <h2 className="font-heading text-3xl font-medium mt-4">Order placed!</h2>
            <p className="text-muted-foreground mt-1">Your order number is</p>
            <p className="font-heading text-4xl font-semibold mt-1">#{placed?.order_number}</p>
            <p className="text-sm text-muted-foreground mt-3">The kitchen has received your order. Total ${placed?.total?.toFixed(2)}.</p>
            <Button onClick={() => setPlaced(null)} style={solidBtn} className="mt-6 hover:opacity-90">Done</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={giftOpen} onOpenChange={setGiftOpen}>
        <DialogContent className="bg-card">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Buy a gift card</DialogTitle></DialogHeader>
          <form onSubmit={buyGift} className="space-y-4">
            <div className="space-y-1.5"><Label>Amount</Label>
              <div className="grid grid-cols-4 gap-2">
                {giftAmounts.map((a) => (
                  <button type="button" key={a} data-testid={`gift-amount-${a}`} onClick={() => setGift({ ...gift, amount: String(a) })}
                    className="py-2 rounded-lg border text-sm font-medium transition-colors"
                    style={String(a) === gift.amount ? { borderColor: brand, color: brand, backgroundColor: `${brand}12` } : {}}>
                    ${a}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5"><Label>Recipient email</Label>
              <Input type="email" value={gift.recipient_email} onChange={(e) => setGift({ ...gift, recipient_email: e.target.value })} placeholder="who is it for?" /></div>
            <div className="space-y-1.5"><Label>Your email</Label>
              <Input type="email" value={gift.buyer_email} onChange={(e) => setGift({ ...gift, buyer_email: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Message (optional)</Label>
              <Textarea value={gift.message} onChange={(e) => setGift({ ...gift, message: e.target.value })} /></div>
            <DialogFooter>
              <Button type="submit" data-testid="gift-checkout-btn" disabled={!gift.amount || giftLoading} style={solidBtn} className="w-full h-11 hover:opacity-90">
                {giftLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : `Pay $${gift.amount || "0"} securely`}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!giftCode} onOpenChange={() => setGiftCode(null)}>
        <DialogContent className="bg-card text-center" data-testid="gift-success">
          <div className="py-4">
            <Gift className="h-14 w-14 mx-auto" style={{ color: brand }} />
            <h2 className="font-heading text-3xl font-medium mt-4">Gift card ready!</h2>
            <p className="text-muted-foreground mt-1">Share this code with the recipient</p>
            <p className="font-mono text-2xl tracking-wider mt-3 bg-muted rounded-lg py-3">{giftCode}</p>
            <Button onClick={() => setGiftCode(null)} style={solidBtn} className="mt-6 hover:opacity-90">Done</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
